import { ReactNode, useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';
import { usePasswordExpiry } from '@/hooks/usePasswordExpiry';
import { IdleTimeoutWarning } from '@/components/auth/IdleTimeoutWarning';
import { ForcePasswordChange } from '@/components/auth/ForcePasswordChange';
import { useToast } from '@/hooks/use-toast';

interface SecurityGateProps {
  children: ReactNode;
}

/**
 * 21 CFR Part 11 Security Gate
 * Provides:
 * - 15-minute idle timeout with warning at 14 minutes
 * - 90-day password expiry enforcement
 * - Password expiry warnings 15 days before expiration
 */
export function SecurityGate({ children }: SecurityGateProps) {
  const { user, session, isAdmin } = useAuth();
  const { toast } = useToast();
  const isAuthenticated = !!user && !!session;

  // Password expiry check
  const { status: passwordStatus, recordPasswordChange } = usePasswordExpiry(user?.id);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const hasChangedPassword = useRef(false);
  const [hasShownExpiryWarning, setHasShownExpiryWarning] = useState(false);
  // Idle timeout - only active when authenticated
  const {
    showWarning,
    secondsRemaining,
    dismissWarning
  } = useIdleTimeout({
    enabled: isAuthenticated,
    warningTimeout: 14 * 60 * 1000, // 14 minutes
    logoutTimeout: 15 * 60 * 1000,  // 15 minutes
  });

  // Once-a-week throttle key (per user). When the user dismisses or
  // changes their password we record the timestamp; the prompt is
  // suppressed for 7 days regardless of expiry status.
  const throttleKey = user?.id ? `pwd_reminder_seen_at:${user.id}` : null;
  const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
  const wasShownThisWeek = (): boolean => {
    if (!throttleKey) return false;
    try {
      const raw = localStorage.getItem(throttleKey);
      if (!raw) return false;
      const ts = Number(raw);
      if (!Number.isFinite(ts)) return false;
      return Date.now() - ts < ONE_WEEK_MS;
    } catch {
      return false;
    }
  };
  const markShownNow = () => {
    if (!throttleKey) return;
    try {
      localStorage.setItem(throttleKey, String(Date.now()));
    } catch {
      /* storage blocked — non-fatal */
    }
  };

  // Handle password expiry — admins are exempt; all other users see
  // the reminder at most once per 7 days.
  useEffect(() => {
    if (!isAuthenticated || !passwordStatus) return;
    if (isAdmin) return; // Admins exempt from password reminder

    if (passwordStatus.expired && !hasChangedPassword.current) {
      if (!wasShownThisWeek()) {
        setShowPasswordChange(true);
      }
    } else if (passwordStatus.warning && !hasShownExpiryWarning) {
      if (!wasShownThisWeek()) {
        toast({
          title: "Password Expiring Soon",
          description: passwordStatus.message || `Your password will expire in ${passwordStatus.daysUntilExpiry} days.`,
          duration: 10000,
        });
        markShownNow();
        setHasShownExpiryWarning(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isAdmin, passwordStatus, hasShownExpiryWarning, toast]);

  // Reset warning state when user changes
  useEffect(() => {
    setHasShownExpiryWarning(false);
    hasChangedPassword.current = false;
  }, [user?.id]);

  const handlePasswordChanged = async () => {
    hasChangedPassword.current = true;
    await recordPasswordChange();
    markShownNow();
    setShowPasswordChange(false);
  };

  const handleDismiss = () => {
    hasChangedPassword.current = true;
    markShownNow();
    setShowPasswordChange(false);
  };

  return (
    <>
      {children}
      
      {/* 21 CFR Part 11: Idle Timeout Warning */}
      <IdleTimeoutWarning
        open={showWarning}
        secondsRemaining={secondsRemaining}
        onStayLoggedIn={dismissWarning}
      />
      
      {/* 21 CFR Part 11: Force Password Change on Expiry */}
      <ForcePasswordChange
        open={showPasswordChange}
        message={passwordStatus?.message || undefined}
        onPasswordChanged={handlePasswordChanged}
        onDismiss={handleDismiss}
      />
    </>
  );
}
